import type { FC } from "react";

import { DEPRECATED_Loading } from "@ui/components/DEPRECATED_Loading";
import { ResponsiveImage } from "@ui/components/ResponsiveImage";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import type { ButtonSize, ButtonVariant } from "@jti/core-components/components/Button";
import { Button } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { HeroScrollBlock } from "../HeroScrollBlock/HeroScrollBlock";
import type { HeroContentProps } from "../types/HeroV2Types";

import { HeaderText } from "./components";

import styles from "./HeroContent.module.scss";

const { sanitize } = dompurify;

const buttonSizes: { size: ButtonSize; responsiveClass: "small-only" | "medium-only" | "large-only" }[] = [
  { size: "sm", responsiveClass: "small-only" },
  { size: "md", responsiveClass: "medium-only" },
  { size: "lg", responsiveClass: "large-only" },
];

const getVariant = (variant: string): ButtonVariant => {
  switch (variant) {
    case "border":
      return "secondary";
    default:
      return "primary";
  }
};

export const HeroContent: FC<HeroContentProps> = ({
  logo,
  logoAlt,
  tagLine,
  title,
  subheadline,
  isH1TagDisabled,
  text,
  discounts,
  cta,
  displayScrollBar,
  isViewportHeight = true,
  isContainerWidth = false,
  gradientPosition,
  gradientPositionTablet,
  gradientPositionMobile,
  isLoading,
  isHeroCardsShown,
  renderImage,
  hasCards,
  logoReference,
  heroContentClassName,
  theme = "dark",
  withBreadcrumbs,
}) => {
  const hasPromotionDisclaimers = discounts && discounts.filter((d) => !!d.priceDisclaimer).length > 0;

  const classes = classNames(styles.heroContent, heroContentClassName, "hero-banner__content", {
    [styles.isContainerWidth]: isContainerWidth,
    [styles.isContainerWidthWithPromotions]: isContainerWidth && hasPromotionDisclaimers,
    [styles.notViewportHeight]: !isViewportHeight,
    [styles.hasGradientTop]: gradientPosition === "top",
    [styles.hasGradientLeft]: gradientPosition === "left",
    [styles.noGradient]: gradientPosition === "none",
    [styles.hasGradientTopTablet]: gradientPositionTablet === "top",
    [styles.hasGradientLeftTablet]: gradientPositionTablet === "left",
    [styles.noGradientTablet]: gradientPositionTablet === "none",
    [styles.hasGradientTopMobile]: gradientPositionMobile === "top",
    [styles.hasGradientLeftMobile]: gradientPositionMobile === "left",
    [styles.noGradientMobile]: gradientPositionMobile === "none",
    [styles.isHeroCardsShown]: isHeroCardsShown,
  });

  const dispatchGTMEvent = (label: string) => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, { cta_name: label, cta_location: "hero-banner" });
  };

  return (
    <div className={classes} data-testid="hero-content">
      <div className={styles.inner}>
        <div
          className={classNames(styles.heroInfoWrapper, "hero-banner__info-wrapper", {
            [styles.heroInfoWrapperWithBreadcrumbs]: withBreadcrumbs,
          })}
        >
          {isLoading ? (
            <DEPRECATED_Loading whiteDots />
          ) : (
            <>
              <div className={styles.heroInfo}>
                <div
                  className={classNames(styles.textContainer, {
                    "dark-theme": theme === "dark",
                    "light-theme": theme === "light",
                  })}
                >
                  <div className={styles.topSection}>
                    {logo && (
                      <div
                        className={classNames(styles.heroLogoContainer, {
                          [styles.terracycleLogo]: logoReference === "PLOOM_TRULY_UNIQUE_TERRACYCLE",
                        })}
                      >
                        {renderImage ? (
                          renderImage({ src: logo, alt: logoAlt ?? "" })
                        ) : (
                          <ResponsiveImage
                            largeSrc={logo}
                            alt={logoAlt ?? ""}
                            hideLoadingState={true}
                            nativeBehaviour
                          />
                        )}
                      </div>
                    )}

                    {tagLine && <div className={styles.heroInfoTagLine}>{tagLine}</div>}
                  </div>

                  <HeaderText title={title} subtitle={subheadline} isH1TagDisabled={isH1TagDisabled} />
                  {text && (
                    <div
                      className={styles.heroInfoText}
                      dangerouslySetInnerHTML={{ __html: sanitize(text, { ADD_ATTR: ["target"] }) }}
                    />
                  )}
                  {discounts && discounts.length >= 1 && (
                    <div className={styles.discountsBlock}>
                      {discounts.map((discount, index) => (
                        <div
                          className={styles.pricingBlock}
                          key={`${discount.currentPrice}${discount.priceDisclaimer}--${index}`}
                        >
                          <div className={styles.priceWrapper}>
                            <div className={styles.priceInnerWrapper}>
                              <span className={classNames(styles.price, styles.currentPrice)}>
                                {discount.currentPrice}
                              </span>
                              <span className={classNames(styles.price, styles.originalPrice)}>
                                {discount.originalPrice}
                              </span>
                            </div>
                            {discount.packInformation && (
                              <div className={styles.packInformation}>{discount.packInformation}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.ctaContainer}>
                  {cta && cta.length >= 1
                    ? buttonSizes.map(({ size: buttonSize, responsiveClass }) => (
                        <div
                          key={buttonSize}
                          className={classNames(styles.ctaWrapper, {
                            [styles.ctaWrapperWithCards]: hasCards,
                            [styles[responsiveClass]]: true,
                          })}
                        >
                          {cta.map((cta, index) => {
                            const key = `${cta.link}--${index}`;
                            const sharedProps = {
                              href: cta.link,
                              target: cta.target,
                              rel: cta.target === "_blank" ? "noreferrer" : "",
                              "data-event": cta.tracking,
                              onClick: () => dispatchGTMEvent(cta.label),
                            };

                            return cta.layout === "chevron" ? (
                              <Button
                                key={key}
                                {...sharedProps}
                                className={`${cta.theme}-theme`}
                                variant="text-muted"
                                iconAfter={LongArrowForwardIcon}
                              >
                                {cta.label}
                              </Button>
                            ) : (
                              <Button
                                key={key}
                                {...sharedProps}
                                className={classNames(styles.bannerCta, `${cta.theme || "light"}-theme`)}
                                variant={getVariant(cta.layout)}
                                size={buttonSize}
                              >
                                {cta.label}
                              </Button>
                            );
                          })}
                        </div>
                      ))
                    : null}
                </div>
              </div>
            </>
          )}
        </div>
        {displayScrollBar && (
          <div className={styles.scrollIndicator}>
            <HeroScrollBlock />
          </div>
        )}
      </div>
    </div>
  );
};
